import ExcelJS from 'exceljs';

const NAVY = 'FF283592';
const HEADER_FILL = 'FFEFEFEF';
const DATE_FILL = 'FFF3F3F3';
const MIN_ROWS_PER_DAY = 9; // keep the familiar look on light days; grows as needed
const HEADER_ROWS = 4;

const navyBold = { name: 'Arial', bold: true, color: { argb: NAVY } };
const solid = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });

// Parse a 'YYYY-MM-DD' string into Y/M/D parts without any timezone involvement.
const parseYMD = (value) => {
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  return { y, m, d };
};

// A 'YYYY-MM-DD' key, derived purely from the string (no Date math).
const dayKey = (value) => String(value).slice(0, 10);

const eachDay = (start, end) => {
  const days = [];
  const s = parseYMD(start);
  const e = parseYMD(end);
  const cur = new Date(Date.UTC(s.y, s.m - 1, s.d));
  const last = Date.UTC(e.y, e.m - 1, e.d);
  while (cur.getTime() <= last) {
    days.push(
      `${cur.getUTCFullYear()}-${String(cur.getUTCMonth() + 1).padStart(2, '0')}-${String(cur.getUTCDate()).padStart(2, '0')}`
    );
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return days; // array of 'YYYY-MM-DD' strings
};

const colLetter = (n) => {
  let s = '';
  let x = n;
  while (x > 0) {
    const m = (x - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    x = Math.floor((x - 1) / 26);
  }
  return s;
};

// data: { project: { key, name }, members: [{ jira_account_id, name, role }],
//         worklogs: [{ issue_key, issue_summary, author_id, logged_hours, logged_date }] }
export async function buildProjectTimesheetWorkbook(data, { startDate, endDate }) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Timesheet', { views: [{ state: 'frozen', ySplit: HEADER_ROWS }] });

  const members = data.members || [];
  const projectKey = data.project?.key || '';

  // Map each member to its Description column, and collect the Hours columns.
  const memberColIndex = {};
  const hoursCols = [];
  members.forEach((m, i) => {
    const descCol = 2 + i * 2;
    const hoursCol = descCol + 1;
    memberColIndex[m.jira_account_id] = descCol;
    hoursCols.push(hoursCol);

    const keyCell = ws.getCell(1, descCol);
    keyCell.value = projectKey;
    keyCell.font = navyBold;
    keyCell.fill = solid(HEADER_FILL);
    keyCell.alignment = { horizontal: 'center' };

    const nameCell = ws.getCell(2, descCol);
    nameCell.value = m.name;
    nameCell.font = navyBold;
    nameCell.alignment = { horizontal: 'center' };

    const roleCell = ws.getCell(3, descCol);
    roleCell.value = m.role || 'Developer';
    roleCell.font = navyBold;
    roleCell.alignment = { horizontal: 'center' };

    const descHdr = ws.getCell(4, descCol);
    descHdr.value = 'Description';
    descHdr.font = navyBold;
    descHdr.alignment = { horizontal: 'center' };

    const hoursHdr = ws.getCell(4, hoursCol);
    hoursHdr.value = 'Hours';
    hoursHdr.font = navyBold;
    hoursHdr.alignment = { horizontal: 'right' };

    ws.getColumn(descCol).width = 27;
    ws.getColumn(hoursCol).width = 9;
  });

  const totalsCol = 2 + members.length * 2;
  const totalsHdr = ws.getCell(4, totalsCol);
  totalsHdr.value = 'Totals';
  totalsHdr.font = navyBold;
  ws.getColumn(1).width = 14;
  ws.getColumn(totalsCol).width = 10;

  // Group worklogs by day, then by member column, preserving order.
  const days = eachDay(startDate, endDate);
  const byDay = {};
  days.forEach((d) => {
    byDay[d] = {};
  });
  // Ensure days that have logs but somehow fall outside the picked range
  // still get represented (defensive — should not normally happen).
  (data.worklogs || []).forEach((w) => {
    const descCol = memberColIndex[w.author_id];
    if (!descCol) return; // logger has no column (shouldn't happen: BE builds members from logs)
    const k = dayKey(w.logged_date);
    if (!byDay[k]) byDay[k] = {};
    if (!byDay[k][descCol]) byDay[k][descCol] = [];
    byDay[k][descCol].push(w);
  });

  const orderedDays = Object.keys(byDay).sort();


  let row = HEADER_ROWS + 1;
  orderedDays.forEach((dStr) => {
    const dayCols = byDay[dStr] || {};
    // Block height = max entries any single member has that day, but never
    // less than MIN_ROWS_PER_DAY. This guarantees NO worklog is ever dropped.
    const maxEntries = Object.values(dayCols).reduce(
      (mx, list) => Math.max(mx, list.length),
      0
    );
    const blockHeight = Math.max(MIN_ROWS_PER_DAY, maxEntries);

    const { y, m, d } = parseYMD(dStr);
    const blockStart = row;

    // Per-row running total of hours across all members' columns.
    const rowTotals = new Array(blockHeight).fill(0);

    // Place each member's worklogs first, accumulating each row's total.
    Object.entries(dayCols).forEach(([descColStr, list]) => {
      const descCol = Number(descColStr);
      list.forEach((w, idx) => {
        const r = blockStart + idx;
        const summary = [w.issue_key, w.issue_summary].filter(Boolean).join(' ');
        const descCell = ws.getCell(r, descCol);
        descCell.value = summary;
        descCell.alignment = { horizontal: 'left', wrapText: true };

        const hours = Number(w.logged_hours) || 0;
        const hoursCell = ws.getCell(r, descCol + 1);
        hoursCell.value = hours;
        hoursCell.numFmt = '0.00';
        hoursCell.alignment = { horizontal: 'right' };

        rowTotals[idx] += hours;
      });
    });

    // Then write the date (every row) + the numeric total (every row, 0.00 when empty).
    for (let r = 0; r < blockHeight; r += 1) {
      const rowNum = blockStart + r;

      const dateCell = ws.getCell(rowNum, 1);
      dateCell.value = new Date(y, m - 1, d, 12, 0, 0);
      dateCell.numFmt = 'mmmm d';
      dateCell.fill = solid(DATE_FILL);
      dateCell.alignment = { horizontal: 'right' };

      const totalCell = ws.getCell(rowNum, totalsCol);
      totalCell.value = Number(rowTotals[r].toFixed(2));
      totalCell.numFmt = '0.00';
      totalCell.fill = solid(DATE_FILL);
      totalCell.alignment = { horizontal: 'right' };

      row += 1;
    }
  });

  return wb;
}

export async function downloadProjectTimesheet(data, { startDate, endDate, fileName }) {
  const wb = await buildProjectTimesheetWorkbook(data, { startDate, endDate });
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'timesheet.xlsx';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}